import { Link, useLocation } from 'react-router-dom'
import { styled } from '@linaria/react'
import { from, minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import { PlateIcon, ChefHatIcon, ClockIcon, ChevronRightIcon } from '../../components/icons'

const destinations = [
  {
    path: '/dishes',
    title: 'Dishes',
    short: 'Pick a plate',
    description: 'Individual meals. Add your favourites to your cart.',
    icon: PlateIcon,
  },
  {
    path: '/menus',
    title: 'Menus',
    short: 'Explore a kitchen',
    description: 'A chef’s collection. Explore what one kitchen is cooking.',
    icon: ChefHatIcon,
  },
  {
    path: '/mealplan',
    title: 'Meal plans',
    short: 'Plan your week',
    description: 'Your meal calendar for weekly food planning.',
    icon: ClockIcon,
  },
] as const

const Style = {
  Nav: styled.nav`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border-bottom: 1px solid ${v.color.borderSubtle};
    gap: ${v.space.xs};
    &[data-expanded='true'] {
      grid-template-columns: minmax(0, 1fr);
      border: 0;
      gap: ${v.space.sm};
    }
    &[data-compact='true'] {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: ${v.space.xs};
    }
    ${from('page')} {
      &[data-expanded='true'] {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: ${v.space.md};
      }
    }
  `,
  Link: styled(Link)`
    min-width: 0;
    color: ${v.color.textSecondary};
    text-decoration: none;
    min-height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${v.space.xs};
    border-bottom: 3px solid transparent;
    font-weight: 800;
    &[aria-current='page'] {
      color: ${v.color.brand};
      border-color: ${v.color.brand};
    }
    &[data-expanded='true'] {
      display: grid;
      grid-template-columns: 56px minmax(0, 1fr);
      grid-template-rows: auto auto auto;
      align-content: start;
      justify-content: stretch;
      align-items: start;
      gap: 6px ${v.space.md};
      padding: 22px 24px;
      border: 1px solid ${v.color.borderSubtle};
      border-radius: ${v.radius.md};
      background: ${v.color.bgSurface};
      min-height: 150px;
    }
    [data-compact='true'] > & {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 10px 8px;
      gap: 3px;
      min-height: auto;
      border-radius: 14px;
    }
    ${from('page')} {
      &[data-expanded='true'] {
        grid-template-columns: 56px minmax(0, 1fr);
        padding: 24px;
      }
      [data-compact='true'] > & {
        padding: ${v.space.md};
      }
    }
    &:focus-visible {
      outline: 3px solid ${v.color.borderFocus};
      outline-offset: 3px;
    }
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        color: ${v.color.brand};
        background: ${v.color.bgElevated};
        border-color: ${v.color.brand};
      }
    }
  `,
  Icon: styled.span`
    color: ${v.color.brand};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    [data-expanded='true'] > & {
      grid-row: 1 / span 3;
      grid-column: 1;
      align-self: start;
      width: 48px;
      height: 48px;
      border-radius: ${v.radius.md};
      background: ${v.color.brandSoft};
      ${from('page')} {
        width: 48px;
        height: 48px;
      }
    }
    [data-compact='true'] & {
      grid-row: auto;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(227, 24, 55, 0.08);
      margin-bottom: 2px;
    }
  `,
  Title: styled.span`
    font-size: ${v.typography.bodySize};
    [data-expanded='true'] > & {
      grid-column: 2;
      grid-row: 1;
      /* The expanded title stays at 1.08rem: h2Size is 22px and would overshoot the narrow phone card. */
      font-size: 1.08rem;
      color: ${v.color.textPrimary};
      align-self: start;
      line-height: 1.2;
    }
    [data-compact='true'] & {
      /* The compact label stays fluid: captionSize at 13px oversizes three labels across a phone. */
      font-size: clamp(0.75rem, 2.7vw, 0.875rem);
      font-weight: 700;
      color: ${v.color.textPrimary};
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      ${from('page')} {
        /* Three equal columns share the row: h2Size at 22px overflows each ellipsised label column. */
        font-size: 1.15rem;
      }
    }
  `,
  Description: styled.span`
    grid-column: 2;
    grid-row: 2;
    font-size: ${v.typography.captionSize};
    line-height: 1.4;
    font-weight: 500;
    color: ${v.color.textSecondary};
    [data-compact='true'] & {
      display: none;
      ${from('page')} {
        display: block;
      }
    }
  `,
  Action: styled.span`
    grid-column: 2;
    grid-row: 3;
    display: inline-flex;
    align-items: center;
    justify-self: start;
    align-self: end;
    gap: ${v.space.xs};
    color: ${v.color.brand};
    font-size: ${v.typography.captionSize};
    font-weight: 800;
    white-space: nowrap;
    margin-top: 8px;
  `,
}

export const FoodNavigation = ({
  expanded = false,
  publicLinks = false,
  compact = false,
}: {
  expanded?: boolean
  publicLinks?: boolean
  compact?: boolean
}) => {
  const { pathname } = useLocation()
  return (
    <Style.Nav
      aria-label="Ways to choose your food"
      data-expanded={expanded}
      data-compact={expanded && compact && !publicLinks}
    >
      {destinations.map(({ path, title, short, description, icon: Icon }) => {
        const isMealPlan = path === '/mealplan'
        const href = publicLinks && isMealPlan ? '/plans' : path
        const displayDescription =
          publicLinks && isMealPlan
            ? 'Plan meals for yourself or your family.'
            : description
        const displayAction = publicLinks && isMealPlan ? 'Start planning' : short

        return (
          <Style.Link
            key={path}
            to={href}
            aria-current={
              !publicLinks &&
              (pathname === path || (path === '/dishes' && pathname.startsWith('/dishes/')))
                ? 'page'
                : undefined
            }
            data-expanded={expanded}
          >
            <Style.Icon>
              <Icon size="md" />
            </Style.Icon>
            <Style.Title>{title}</Style.Title>
            {expanded && (
              <>
                <Style.Description>{displayDescription}</Style.Description>
                <Style.Action>
                  {displayAction}
                  <ChevronRightIcon size="sm" />
                </Style.Action>
              </>
            )}
          </Style.Link>
        )
      })}
    </Style.Nav>
  )
}
